import _ from "lodash";
import { useEffect, useState } from "react";
import { Button, Dropdown, DropdownItemProps, Form, Header, Input, Modal, Segment, Table } from "semantic-ui-react";
import { dialogControl } from "../../controls/dialog-control";
import { notificationControl } from "../../controls/notification-control";
import { useCurrentUser } from "../../hooks/auth-hook";
import { ModalControlData, useModalControl, useRequest } from "../../hooks/commonHooks";
import { useFormData } from "../../hooks/formHooks";
import { hostUrl, restRequest, waitForLongTimeTask } from "../../http";
import { MeasurementData, UpdateLimitCriterion, UpdateLimitMeasurementData, UpdateLimitResult } from "../../models/update_limit_model";
import { downloadCSV, downloadTXT, fieldError, formatDateTime } from "../../utils";
import { FixedXValuesComponent } from "../commons/FixedXValuesComponent";
import { RoomUncertainty } from "../commons/RoomUncertaintyComponent";
import { FilterEditor } from "./FilterEditor";

export function UpdateLimitMeasurementComponent(props: { updateId: number }) {
    const currentUser = useCurrentUser();
    const [list, setList] = useState<UpdateLimitMeasurementData[]>([]);

    const editModalControl = useModalControl();
    const [currentEditId, setCurrentEditId] = useState<number>();
    const [reloadNumber, setReloadNumber] = useState(0);

    useEffect(() => {
        restRequest<UpdateLimitMeasurementData[]>(`update_limit/measurements/${props.updateId}/search`).then(setList);
    }, [reloadNumber]);

    return <>
        <Table striped>
            <Table.Header>
                <Table.Row>
                    <th>Measurement Name</th>
                    <th>Export File Name</th>
                    <th>Created By</th>
                    <th>Created At</th>
                    <th>{currentUser.permissions.update_limit.measurement.create && <Button color="blue" icon="add" content="Measurement" size="mini" onClick={e => {
                        setCurrentEditId(undefined);
                        editModalControl.setOpen(true);
                    }} />}</th>
                </Table.Row>
            </Table.Header>
            <Table.Body>
                {list.map(obj => {
                    return <tr key={obj.id}>
                        <td>{obj.measurement_key.split('_le-witt_')[0]}</td>
                        <td>{obj.measurement_key.split('_le-witt_')[1]}</td>
                        <td>{obj.created_by_name}</td>
                        <td>{formatDateTime(obj.created_at)}</td>
                        <td>
                            {currentUser.permissions.update_limit.measurement.update && <Button onClick={e => {
                                setCurrentEditId(obj.id);
                                editModalControl.setOpen(true);
                            }} color="blue" icon="edit" size="mini" />}

                            <Button target="_blank" as="a" href={`/update_limit_plot/${obj.id}`} color="blue" icon="chart line" size="mini" />

                            {currentUser.permissions.update_limit.measurement.delete && <Button onClick={e => {
                                dialogControl.deletionConfirm(obj.measurement_key.split('_le-witt_').join('  '), argee => {
                                    if (argee) {
                                        return restRequest(`update_limit/measurements/${obj.id}`, null, 'delete').then(() => {
                                            setReloadNumber(n => n + 1);
                                        })
                                    }
                                    return Promise.resolve()
                                })
                            }} color="red" icon="delete" size="mini" />}
                        </td>
                    </tr>
                })}
            </Table.Body>
        </Table>

        <Editor id={currentEditId} updateId={props.updateId} modelControl={editModalControl} onFinish={result => {
            setReloadNumber(n => n + 1);
            // editModalControl.setOpen(false)
            setReloadNumber(n => n + 1);
        }} usedMeasurementKeys={list.map(o => o.measurement_key)}></Editor>
    </>
}

function Editor(props: { id?: number, updateId: number, allRoles?: { id: number, name: string }[], modelControl: ModalControlData, usedMeasurementKeys: string[], onFinish: (data: UpdateLimitMeasurementData) => void }) {
    const currentUser = useCurrentUser();
    const { updateId, modelControl, usedMeasurementKeys, onFinish } = props;
    const [id, setId] = useState(props.id);
    const [data, setField, setData] = useFormData<UpdateLimitMeasurementData>({} as any);
    const [invalidError, setInvalidError] = useState<any>({});
    const [loading, setLoading] = useState(false);
    const [loadingMeasurement, setLoadingMeasurement] = useState(false);
    const [measurements, setMeasurements] = useState<MeasurementData[]>([]);
    const [measurementOptions, setMeasurementOptions] = useState<DropdownItemProps[]>([]);
    const criteriaModalControl = useModalControl();
    const [editingCriterion, setEditingCriterion] = useState<UpdateLimitCriterion & { index?: number }>({} as any);
    const [invalidCriterionError, setInvalidCriterionError] = useState<{ [k in keyof UpdateLimitCriterion]: string }>();
    const calculateResultModalControl = useModalControl();
    const [editingCalculateResult, setEditingCalculateResult] = useState<UpdateLimitResult & { index?: number }>({} as any);
    const [invalidCalculateResultError, setInvalidCalculateResultError] = useState<{ [k in keyof UpdateLimitResult]: string }>();
    const testResultModalControl = useModalControl();
    const [editingTestResult, setEditingTestResult] = useState<UpdateLimitResult & { index?: number }>({} as any);
    const [invalidTestResultError, setInvalidTestResultError] = useState<{ [k in keyof UpdateLimitResult]: string }>();
    const [fixedXValuesOptions, setFixedXValuesOptions] = useState<DropdownItemProps[]>([]);
    const fixedXValuesModalControl = useModalControl();
    const [refreshFixedXValues, setRefreshFixedXValues] = useState<number>(0);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setId(props.id);
    }, [props.id]);

    const isCreate = !id;

    useEffect(() => {
        restRequest<DropdownItemProps[]>('commons/fixed_x_values/search_for_selection', null, 'get').then(values => setFixedXValuesOptions([{ key: '', value: '', test: 'Empty' }, ...values]));
    }, [refreshFixedXValues]);

    useEffect(() => {
        setLoadingMeasurement(true);
        restRequest<MeasurementData[]>('update_limit/measurements_config/' + updateId).then(ms => {
            setMeasurements(ms);
            const measurementOptions: DropdownItemProps[] = ms.map(m => {
                const key = [m.measurement_name, m.export_file_name].join('_le-witt_');
                return { key, value: key, text: `${m.measurement_name} (${m.export_file_name})` }
            });
            setMeasurementOptions(measurementOptions);
        }).finally(() => setLoadingMeasurement(false));
    }, [updateId]);

    useEffect(() => {
        setData({} as any);
        setInvalidError({});
        if (id) {
            setLoading(true);
            restRequest<UpdateLimitMeasurementData>('update_limit/measurements/' + id).then(setData).finally(() => setLoading(false))
        }
    }, [id])

    const onCriterionSave = (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event && event.preventDefault();

        const invalidErrors = {} as any;
        if (!editingCriterion.name || !editingCriterion.name.trim()) {
            invalidErrors.name = "it's required"
        } else if (criteria.find((c, i) => (editingCriterion.index === undefined || editingCriterion.index !== i) && c.name === editingCriterion.name)) {
            invalidErrors.name = "it's used"
        }

        if (!editingCriterion.sigma) {
            invalidErrors.sigma = "it's required"
        }

        setInvalidCriterionError(invalidErrors);
        if (!_.isEmpty(invalidErrors)) {
            return false;
        }

        if (editingCriterion.index !== undefined) {
            const index = editingCriterion.index;
            delete editingCriterion.index;
            setData(d => ({ ...d, criteria: d.criteria.map((c, i) => i === index ? editingCriterion : c) }))
        } else {
            setData(d => ({ ...d, criteria: [...(d.criteria || []), editingCriterion] }));
        }

        criteriaModalControl.setOpen(false);
    }

    const onCalculateResultSave = (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event && event.preventDefault();

        const invalidErrors: any = {};
        if (!editingCalculateResult.name || !editingCalculateResult.name.trim()) {
            invalidErrors.name = "it's required";
        } else if (calculate_results.find((cr, i) => (editingCalculateResult.index === undefined || editingCalculateResult.index !== i) && cr.name == editingCalculateResult.name)) {
            invalidErrors.name = "it's used"
        }

        setInvalidCalculateResultError(invalidErrors);
        if (!_.isEmpty(invalidErrors)) {
            return false;
        }

        if (editingCalculateResult.index !== undefined) {
            const index = editingCalculateResult.index;
            delete editingCalculateResult.index;
            setData(d => ({ ...d, calculate_results: d.calculate_results.map((c, i) => i === index ? editingCalculateResult : c) }))
        } else {
            setData(d => ({ ...d, calculate_results: [...(d.calculate_results || []), editingCalculateResult] }));
        }

        calculateResultModalControl.setOpen(false);
    }

    const onTestResultSave = (event?: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        event && event.preventDefault();

        const invalidErrors: any = {};
        if (!editingTestResult.name || !editingTestResult.name.trim()) {
            invalidErrors.name = "it's required";
        } else if (test_results.find((cr, i) => (editingTestResult.index === undefined || editingTestResult.index !== i) && cr.name == editingTestResult.name)) {
            invalidErrors.name = "it's used"
        }

        setInvalidTestResultError(invalidErrors);
        if (!_.isEmpty(invalidErrors)) {
            return false;
        }

        if (editingTestResult.index !== undefined) {
            const index = editingTestResult.index;
            delete editingTestResult.index;
            setData(d => ({ ...d, test_results: d.test_results.map((c, i) => i === index ? editingTestResult : c) }))
        } else {
            setData(d => ({ ...d, test_results: [...(d.test_results || []), editingTestResult] }));
        }
        testResultModalControl.setOpen(false);
    }

    const criteria = data.criteria || [];
    const calculate_results = data.calculate_results || [];
    const test_results = data.test_results || [];
    return <>
        <Modal {...modelControl.props} size="fullscreen">
            <Modal.Header>{isCreate ? 'Create' : 'Update'} Measurment </Modal.Header>
            <Modal.Content>
                <Form as="div" loading={loading}>
                    <Form.Group widths="equal" inline>
                        <Form.Input label="Measurement: " error={fieldError('measurement_key', invalidError)}>
                            <Dropdown loading={loadingMeasurement} disabled={!isCreate} value={data.measurement_key || ''} onChange={(e, dropdownData) => setData(d => ({ ...d, measurement_key: dropdownData.value as string }))} options={measurementOptions.filter(m => m.value == data.measurement_key || !usedMeasurementKeys.includes(m.value as string))} search selection></Dropdown>
                        </Form.Input>

                        <Form.Input label={<><label style={{ display: 'inline-block', marginRight: '1rem' }}>Fixed X Values</label> <Button onClick={e => fixedXValuesModalControl.setOpen(true)} compact color="blue" icon="edit outline" size="mini" as="a"></Button></>} error={fieldError('fixed_x_values_id', invalidError)}>
                            <Dropdown name="fixed_x_values_id" value={data.fixed_x_values_id || ''} onChange={(e, d) => setData(value => ({ ...value, fixed_x_values_id: d.value as number }))} options={fixedXValuesOptions} search selection></Dropdown>
                        </Form.Input>
                    </Form.Group>
                </Form>
                {data.measurement_key && <Table celled structured>
                    <Table.Header>
                        <Table.Row>
                            <th>
                                <Button icon="add" content="Add Criterion" size="mini" onClick={e => {
                                    setEditingCriterion({} as any);
                                    setInvalidCriterionError({} as any);
                                    criteriaModalControl.setOpen(true);
                                }} />
                            </th>
                            {criteria.map((criterion, index) => <th key={index} colSpan={calculate_results.length}>
                                {criterion.name} &nbsp;
                                <Button onClick={e => {
                                    setEditingCriterion({ ...criterion, index });
                                    setInvalidCriterionError({} as any);
                                    criteriaModalControl.setOpen(true);
                                }} as="a" size="mini" compact icon="edit"></Button>

                                <Button onClick={e => {
                                    dialogControl.deletionConfirm(criterion.name, agree => {
                                        if (agree) {
                                            setData(d => ({ ...d, criteria: d.criteria.filter((c, i) => i !== index) }));
                                        }
                                        return Promise.resolve();
                                    });
                                }} as="a" size="mini" compact color="red" icon="delete"></Button>

                            </th>)}

                        </Table.Row>
                        <Table.Row>
                            <th>
                                <Button icon="add" content="Add Calculate Result" size="mini" onClick={e => {
                                    setEditingCalculateResult({} as any);
                                    setInvalidCalculateResultError({} as any);
                                    calculateResultModalControl.setOpen(true);
                                }} />
                            </th>
                            {criteria.map((criterion, index) => {
                                return calculate_results.map((cr, ci) => <th key={index + '_' + ci}>
                                    {cr.name} &nbsp;
                                    {index === 0 && <>
                                        <Button onClick={e => {
                                            setEditingCalculateResult({ ...cr, index: ci });
                                            setInvalidCalculateResultError({} as any);
                                            calculateResultModalControl.setOpen(true);
                                        }} as="a" size="mini" compact icon="edit"></Button>

                                        <Button onClick={e => {
                                            dialogControl.deletionConfirm(cr.name, agree => {
                                                if (agree) {
                                                    setData(d => ({ ...d, calculate_results: d.calculate_results.filter((c, i) => i !== ci) }));
                                                }
                                                return Promise.resolve();
                                            });
                                        }} as="a" size="mini" compact color="red" icon="delete"></Button>
                                    </>}

                                    <Button.Group size="mini" compact>
                                        <Button as="a" onClick={e => {
                                            e.preventDefault();
                                            let limitData = null;
                                            try {
                                                limitData = data.results[data.criteria_keys[index]].limits[data.calculate_result_keys[ci]];
                                            } catch (e) {

                                            }

                                            if (limitData) {
                                                const ch1Limit = limitData[1];
                                                const ch2Limit = limitData[2];

                                                const data: string[][] = [];
                                                data.push(['CH1', '', ''])
                                                data.push(['X', 'Lower', 'Upper'])
                                                ch1Limit.x_values.forEach((x, i) => {
                                                    const values: string[] = [];
                                                    values.push(x.toFixed(5));
                                                    values.push(ch1Limit.lower_y_values[i].toFixed(5));
                                                    values.push(ch1Limit.upper_y_values[i].toFixed(5));

                                                    data.push(values);
                                                })

                                                if (ch2Limit) {
                                                    data[0].push('CH2')
                                                    data[1].push('X')
                                                    data[1].push('Lower')
                                                    data[1].push('Upper')

                                                    ch2Limit.x_values.forEach((x, i) => {
                                                        const values = data[i + 2] || ['', '', ''];
                                                        values.push(x.toFixed(5));
                                                        values.push(ch2Limit.lower_y_values[i].toFixed(5));
                                                        values.push(ch2Limit.upper_y_values[i].toFixed(5));

                                                        data.push(values);
                                                    })
                                                }

                                                downloadCSV(data.map(v => v.join(',')).join('\n'), criterion.name + '_' + cr.name + '.csv')
                                            }


                                        }}>CSV</Button>
                                        <Button as="a" onClick={e => {
                                            e.preventDefault();
                                            let limitData = null;
                                            try {
                                                limitData = data.results[data.criteria_keys[index]].limits[data.calculate_result_keys[ci]];
                                            } catch (e) {

                                            }

                                            if (limitData) {
                                                const result: any = {}
                                                const ch1Limit = limitData[1];
                                                const ch2Limit = limitData[2];

                                                result['channel1'] = { lower_limit: { x_values: [], y_values: [] }, upper_limit: { x_values: [], y_values: [] } }
                                                ch1Limit.x_values.forEach((x, i) => {
                                                    result['channel1'].lower_limit.x_values.push(x);
                                                    result['channel1'].lower_limit.y_values.push(ch1Limit.lower_y_values[i]);
                                                    result['channel1'].upper_limit.x_values.push(x);
                                                    result['channel1'].upper_limit.y_values.push(ch1Limit.upper_y_values[i]);
                                                })

                                                if (ch2Limit) {
                                                    result['channel2'] = { lower_limit: { x_values: [], y_values: [] }, upper_limit: { x_values: [], y_values: [] } }

                                                    ch2Limit.x_values.forEach((x, i) => {
                                                        result['channel2'].lower_limit.x_values.push(x);
                                                        result['channel2'].lower_limit.y_values.push(ch1Limit.lower_y_values[i]);
                                                        result['channel2'].upper_limit.x_values.push(x);
                                                        result['channel2'].upper_limit.y_values.push(ch1Limit.upper_y_values[i]);
                                                    })
                                                }

                                                const jsonStr = JSON.stringify(result, (k, v) => {
                                                    if (typeof v === 'number' && !Number.isInteger(v) && !Number.isNaN(v)) {
                                                        return 'n-f' + v.toFixed(5).replace(/\.?0+$/, '') + '^-'
                                                    } else {
                                                        return v;
                                                    }
                                                }, 2).replace(/"n-f(-?[0-9\.]+)\^-"/g, "$1").replace(/\s*([0-9\.],)\n/g, "$1 ").replace(/\s+([0-9\.],)/g, "$1");

                                                downloadTXT(jsonStr, criterion.name + '_' + cr.name + '.json')
                                            }
                                        }}>JSON</Button>
                                    </Button.Group>
                                </th>)
                            })}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        {test_results.map((testResult, testResultIndex) => {
                            return <tr key={testResultIndex}>
                                <td>
                                    {testResult.name} &nbsp;
                                    <Button onClick={e => {
                                        setEditingTestResult({ ...testResult, index: testResultIndex });
                                        setInvalidTestResultError({} as any);
                                        testResultModalControl.setOpen(true);
                                    }} as="a" size="mini" compact icon="edit"></Button>

                                    <Button onClick={e => {
                                        dialogControl.deletionConfirm(testResult.name, agree => {
                                            if (agree) {
                                                setData(d => ({ ...d, test_results: d.test_results.filter((c, i) => i !== testResultIndex) }));
                                            }
                                            return Promise.resolve();
                                        });
                                    }} as="a" size="mini" compact color="red" icon="delete"></Button>
                                </td>
                                {criteria.map((criterion, criterionIndex) => {
                                    return calculate_results.map((calculateResult, calculateResultIndex) => <td key={calculateResultIndex}>{generateTestResult(data, criterionIndex, calculateResultIndex, testResultIndex)}</td>)
                                })}
                            </tr>
                        })}

                        <tr>
                            <td colSpan={criteria.length * calculate_results.length + 1}>
                                <Button icon="add" content="Add Test Result" size="mini" onClick={e => {
                                    setEditingTestResult({} as any);
                                    setInvalidTestResultError({} as any);
                                    testResultModalControl.setOpen(true);
                                }} />
                            </td>
                        </tr>
                    </Table.Body>
                </Table>}
            </Modal.Content>
            <Modal.Actions>
                <Button primary loading={saving} onClick={e => {
                    e.preventDefault();
                    setSaving(true);

                    restRequest<{id: number, task_key: string}>(`update_limit/measurements/${isCreate ? updateId : id}`, data, isCreate ? 'post' : 'put').then(({id, task_key}) => {
                        setId(id);

                        return waitForLongTimeTask<number>(task_key).then((id) => {
                            return restRequest<UpdateLimitMeasurementData>(`update_limit/measurements/${id}`).then((result) => {
                                onFinish(result);
                                setData(result);
                                setInvalidError({});

                                notificationControl.showSuccess('job done!');
                            });
                        });
                    }, resp => {
                        if (resp.status === 2) {
                            setInvalidError(resp.data);
                        }
                    }).finally(() => setSaving(false));
                }}>Save</Button>
                <Button onClick={e => modelControl.setOpen(false)} loading={saving}>Close</Button>
            </Modal.Actions>
        </Modal>

        <Modal open={criteriaModalControl.props.open} onClose={() => {
            criteriaModalControl.props.onClose();
        }} size="large" closeIcon="close">
            <Modal.Header> Creteria </Modal.Header>
            <Modal.Content>
                <CriteriaEditor value={editingCriterion} invalidError={invalidCriterionError} onChange={setEditingCriterion} onSubmit={onCriterionSave}></CriteriaEditor>
            </Modal.Content>
            <Modal.Actions>
                <Button primary onClick={onCriterionSave}>Save</Button>
                <Button onClick={e => criteriaModalControl.setOpen(false)}>Cancel</Button>
            </Modal.Actions>
        </Modal>

        <Modal open={calculateResultModalControl.props.open} onClose={() => {
            calculateResultModalControl.props.onClose();
        }} size="large" closeIcon="close">
            <Modal.Header> Calculate Result </Modal.Header>
            <Modal.Content>
                <ResultEditor value={editingCalculateResult} measurement_key={data.measurement_key} measurements={measurements} invalidError={invalidCalculateResultError} onChange={setEditingCalculateResult} onSubmit={onCalculateResultSave}></ResultEditor>
            </Modal.Content>
            <Modal.Actions>
                <Button primary onClick={onCalculateResultSave} as="a">Save</Button>
                <Button onClick={e => calculateResultModalControl.setOpen(false)}>Cancel</Button>
            </Modal.Actions>
        </Modal>

        <Modal open={testResultModalControl.props.open} onClose={() => {
            testResultModalControl.props.onClose();
        }} size="large" closeIcon="close">
            <Modal.Header> Test Result </Modal.Header>
            <Modal.Content>
                <ResultEditor value={editingTestResult} measurement_key={data.measurement_key} measurements={measurements} invalidError={invalidTestResultError} onChange={setEditingTestResult} onSubmit={onTestResultSave}></ResultEditor>
            </Modal.Content>
            <Modal.Actions>
                <Button primary onClick={onTestResultSave}>Save</Button>
                <Button onClick={e => testResultModalControl.setOpen(false)}>Cancel</Button>
            </Modal.Actions>
        </Modal>

        <Modal open={fixedXValuesModalControl.props.open} onClose={() => {
            fixedXValuesModalControl.props.onClose();
            setRefreshFixedXValues(n => n + 1);
        }} size="large" closeIcon="close">
            <Modal.Header> Fixed X Valuees </Modal.Header>
            <Modal.Content>
                <FixedXValuesComponent />
            </Modal.Content>
        </Modal>
    </>
}

function CriteriaEditor(props: { value: UpdateLimitCriterion, invalidError?: { [k in keyof UpdateLimitCriterion]: string }, onChange: (value: UpdateLimitCriterion) => void, onSubmit: (event: React.FormEvent<HTMLFormElement>) => void }) {
    const { value, invalidError, onChange, onSubmit } = props;
    const [roomUncertaintyOptions, setRoomUncertaintyOptions] = useState<DropdownItemProps[]>([]);
    const roomUncertaintyModalControl = useModalControl();
    const [refreshRoomUncertainty, setRefreshRoomUncertainty] = useState<number>(0);

    useEffect(() => {
        restRequest<DropdownItemProps[]>('commons/room_uncertainties/search_for_selection', null, 'get').then(values => setRoomUncertaintyOptions([{ key: '', value: '', test: 'Empty' }, ...values]));
    }, [refreshRoomUncertainty]);

    return <>
        <Form onSubmit={onSubmit}>
            <Form.Group widths="equal">
                <Form.Input label="Name" required error={fieldError('name', invalidError)}>
                    <Input name="name" value={value.name || ''} onChange={(e, d) => onChange({ ...value, name: d.value })} />
                </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Input label="Sigma" required error={fieldError('sigma', invalidError)}>
                    <Input name="sigma" type="number" value={value.sigma || ''} onChange={(e, d) => onChange({ ...value, sigma: d.value ? parseFloat(d.value) : 0 })} />
                </Form.Input>
            </Form.Group>
            <Form.Group widths="equal">
                <Form.Input label={<><label style={{ display: 'inline-block', marginRight: '1rem' }}>Room Uncertainty</label> <Button onClick={e => roomUncertaintyModalControl.setOpen(true)} compact color="blue" icon="edit outline" size="mini" as="a"></Button></>} error={fieldError('room_uncertainty_id', invalidError)}>
                    <Dropdown name="room_uncertainty_id" value={value.room_uncertainty_id || ''} onChange={(e, d) => onChange({ ...value, room_uncertainty_id: d.value as number })} options={roomUncertaintyOptions} search selection></Dropdown>
                </Form.Input>
            </Form.Group>
            <input type="submit" name="submit" style={{ display: 'none' }} />
        </Form>
        <Modal open={roomUncertaintyModalControl.props.open} onClose={() => {
            roomUncertaintyModalControl.props.onClose();
            setRefreshRoomUncertainty(n => n + 1);
        }} size="large" closeIcon="close">
            <Modal.Header> Room Uncertainty </Modal.Header>
            <Modal.Content>
                <RoomUncertainty />
            </Modal.Content>
        </Modal>
    </>
}

function ResultEditor(props: { value: UpdateLimitResult, invalidError?: { [k in keyof UpdateLimitResult]: string | any }, measurement_key: string, measurements: MeasurementData[], onChange: (value: UpdateLimitResult) => void, onSubmit: (event: React.FormEvent<HTMLFormElement>) => void }) {
    const { value, invalidError, measurement_key, onSubmit, measurements, onChange } = props;

    return <>
        <Form onSubmit={onSubmit}>
            <Form.Group widths="equal">
                <Form.Input label="Name" required error={fieldError('name', invalidError)}>
                    <Input name="name" value={value.name || ''} onChange={(e, d) => onChange({ ...value, name: d.value })} />
                </Form.Input>
            </Form.Group>
            <Segment.Group size="mini">
                {(value.filters || []).map((f, i) => <Segment key={i}>
                    <Header size="tiny">Filter {i + 1}
                        <Button style={{fontWeight: 'normal'}} onClick={e => {
                            e.preventDefault();
                            dialogControl.confirm(`Are you sure to delete Filter ${i + 1}`, agree => {
                                if (agree) {
                                    onChange({...value, filters: value.filters.filter((ff, fi) => fi !== i)});
                                }
                                return Promise.resolve();
                            })
                        }} size="mini" icon="delete" color="red" floated="right"></Button>
                    </Header>
                    <FilterEditor value={f} measurement_key={measurement_key} measurements={measurements} invalidError={(invalidError?.filters || [])[i]} onChange={uf => {
                        onChange({ ...value, filters: value.filters.map((of, oi) => oi === i ? uf : of) });
                    }}></FilterEditor>
                    
                </Segment>)}

                <Segment>
                    <Button as="a" color="blue" icon="add" content="Add Result" size="mini" onClick={e => {
                        onChange({ ...value, filters: [...(value.filters || []), {} as any] })
                    }} />
                </Segment>
            </Segment.Group>
        </Form>
    </>
}


function generateTestResult(data: UpdateLimitMeasurementData, criterionIndex: number, calculateResultIndex: number, testResultIndex: number) {
    if (!data.criteria_keys) {
        return '-';
    }
    const criterionKey = data.criteria_keys[criterionIndex];
    if (data.results && data.results[criterionKey]) {
        const result = data.results[criterionKey];
        if (result && result.tests) {
            const calculateResultKey = data.calculate_result_keys[calculateResultIndex];
            const tests = result.tests[calculateResultKey];
            if (tests) {
                const testResultKey = data.test_result_keys[testResultIndex];
                const statistics = tests[testResultKey];

                const channels = _.keys(statistics).map(channel => parseInt(channel)).filter(channel => statistics[channel].total > 0);
                return channels.map(channel => {
                    const info = statistics[channel];

                    return <div key={channel}>
                        {`CH${channel}: ` + (100.0 * info.pass / info.total).toFixed(2) + '%'}
                        &nbsp;&nbsp;
                        <Button href={hostUrl(`update_limit/measurements/export_testing_result_files/${data.id}/${criterionKey}/${calculateResultKey}/${testResultKey}/${channel}`)} compact as="a" size="mini" target="_blank">Files</Button>
                    </div>
                })
            }
        }
    }

    return '-';
}